'use strict';

/**
 * filterApps(apps, account, options) — pure gate deciding which app templates
 * an account may see in the app store.
 *
 * Two INDEPENDENT rollout populations are stamped onto the app-template row by
 * rocket-api's apps-import, straight from the rocket-apps package schema.json:
 *
 *   - `beta: true`  — the wide beta programme (e.g. sky-news-live)
 *   - `alpha: true` — the narrow alpha programme (achievers, workday)
 *
 * A gated app is shown only when the matching account flag is true. The two
 * gates are parallel and never merged: a beta account must not see alpha apps
 * and vice versa. An account may hold both flags at once.
 *
 * Anything in realm 'rocket' carrying neither gate is ungated and always
 * visible. Note this is a positive test for "no gate" rather than the old
 * `beta: null` / `beta: false` enumeration, which silently dropped rows whose
 * flag was simply absent (exactly the shape an alpha-only app row has).
 *
 * @param {Array<object>} apps - app templates from AppTemplate.find()
 * @param {{alpha?: boolean, beta?: boolean}|null|undefined} account
 * @param {{subAccountId?: number}} [options]
 * @returns {Array<object>}
 */
function filterApps(apps, account, options) {
    const list = apps || [];
    // Strict boolean (fails closed): a truthy/string flag ('yes', 'true') from
    // the API must NOT grant the gate. Mirrors can-manage-magic-link.js +
    // filter-integration-templates.js.
    const alpha = !!(account && account.alpha === true);
    const beta = !!(account && account.beta === true);
    const subAccountId = options && options.subAccountId;

    const isGated = (app, flag) => app[flag] === true;

    // Rocket apps carrying neither rollout gate — always visible.
    const rocketApps = list.filter(
        (app) => app.realm === 'rocket' && !isGated(app, 'alpha') && !isGated(app, 'beta')
    );

    // Rocket alpha apps — only for accounts on the alpha programme.
    const alphas = alpha ? list.filter((app) => app.realm === 'rocket' && isGated(app, 'alpha')) : [];

    // Rocket beta apps — only for accounts on the beta programme.
    const betas = beta ? list.filter((app) => app.realm === 'rocket' && isGated(app, 'beta')) : [];

    // Custom apps, optionally narrowed to the selected sub-account.
    const customs = list.filter((app) => app.realm === 'custom');
    const subAccounts = subAccountId ? customs.filter((app) => app.subAccountId === subAccountId) : customs;

    // An app flagged BOTH alpha and beta would otherwise appear twice for an
    // account holding both flags; de-duplicate while preserving order.
    const combined = [].concat(rocketApps, alphas, betas, subAccounts);
    const seen = [];
    return combined.filter((app) => {
        if (seen.indexOf(app) !== -1) return false;
        seen.push(app);
        return true;
    });
}

// CommonJS export for Mocha unit tests (guarded so it is a no-op inside the
// concatenated Angular bundle, where `filterApps` is a plain global — mirrors
// the build-magic-link-url.js / can-manage-magic-link.js pattern).
if (typeof module !== 'undefined' && typeof exports !== 'undefined' && module.exports === exports) {
    module.exports = { filterApps };
}
